import { unwrap } from "@sagot/api-client";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { fetchAllSources } from "../../../lib/all-sources";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { isProcessing } from "../shared/ingestion";
import { dkKeys } from "../shared/keys";
import type { SourceView } from "../shared/types";
import type { SourceEventsState } from "../shared/use-source-events";

/** The list is filtered client-side, so every page of the endpoint is loaded (`fetchAllSources`). */
const POLL_MS = 5_000;

export function useSources(events: SourceEventsState): UseQueryResult<SourceView[]> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: dkKeys.sources(wsId),
    queryFn: () => fetchAllSources(api),
    // Polling fallback (CLAUDE.md rule 8) while the SSE stream is down and something is processing.
    refetchInterval: (query) => (events !== "live" && (query.state.data ?? []).some((s) => isProcessing(s.status)) ? POLL_MS : false),
  });
}

function useInvalidateSources() {
  const queryClient = useQueryClient();
  const wsId = useWorkspaceId();
  return () => queryClient.invalidateQueries({ queryKey: dkKeys.sources(wsId) });
}

export function useReindex() {
  const api = useApi();
  const invalidate = useInvalidateSources();
  return useMutation({
    mutationFn: (sourceId: string) => unwrap(api.POST("/v1/sources/{source_id}/reindex", { params: { path: { source_id: sourceId } } })),
    onSuccess: invalidate,
  });
}

export function useReindexAll() {
  const api = useApi();
  const invalidate = useInvalidateSources();
  return useMutation({ mutationFn: () => unwrap(api.POST("/v1/sources/reindex-all")), onSuccess: invalidate });
}

export function useDeleteSource() {
  const api = useApi();
  const invalidate = useInvalidateSources();
  return useMutation({
    mutationFn: (sourceId: string) => unwrap(api.DELETE("/v1/sources/{source_id}", { params: { path: { source_id: sourceId } } })),
    onSuccess: invalidate,
  });
}

export function useUpdateAudiences() {
  const api = useApi();
  const invalidate = useInvalidateSources();
  return useMutation({
    mutationFn: ({ sourceId, audiences }: { sourceId: string; audiences: number[] }) =>
      unwrap(api.PATCH("/v1/sources/{source_id}", { params: { path: { source_id: sourceId } }, body: { audiences, clear_valid_until: false } })),
    onSuccess: invalidate,
  });
}

export interface WebsiteInput {
  url: string;
  maxPages: number;
  include: string[];
  exclude: string[];
  audiences: number[];
  /** Agents that answer from the new source (needs `agents.edit` on each; the API enforces it). */
  agentIds?: readonly string[];
  /** Re-crawl cadence; the Knowledge Base page keeps crawls manual, onboarding keeps them fresh weekly. */
  schedule?: "manual" | "daily" | "weekly";
}

export function useDiscoverSite() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: Omit<WebsiteInput, "audiences">) =>
      unwrap(api.POST("/v1/sources/crawl/discover", { body: { url: input.url, max_pages: input.maxPages, include: input.include, exclude: input.exclude } })),
  });
}

export type NewSource =
  | { type: "website"; input: WebsiteInput }
  | { type: "qa"; question: string; answer: string; audiences: number[]; agentIds?: readonly string[] }
  | { type: "text"; title: string; text: string; audiences: number[]; agentIds?: readonly string[] };

const TITLE_MAX = 300;

function hostTitle(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.hostname}${parsed.pathname === "/" ? "" : parsed.pathname}`.slice(0, TITLE_MAX);
  } catch {
    return url.slice(0, TITLE_MAX);
  }
}

function linked(agentIds: readonly string[] | undefined) {
  return agentIds && agentIds.length > 0 ? { agent_ids: [...agentIds] } : {};
}

function bodyOf(source: NewSource) {
  if (source.type === "website") {
    const { url, maxPages, include, exclude, audiences, agentIds, schedule = "manual" } = source.input;
    return { type: "website" as const, title: hostTitle(url), url, max_pages: maxPages, include, exclude, audiences, schedule, ...linked(agentIds) };
  }
  if (source.type === "qa") {
    return { type: "qa" as const, title: source.question.slice(0, TITLE_MAX), pairs: [{ question: source.question, answer: source.answer }], audiences: source.audiences, ...linked(source.agentIds) };
  }
  return { type: "text" as const, title: source.title, text: source.text, audiences: source.audiences, ...linked(source.agentIds) };
}

export function useCreateSource() {
  const api = useApi();
  const invalidate = useInvalidateSources();
  return useMutation({
    mutationFn: (source: NewSource) => unwrap(api.POST("/v1/sources", { body: bodyOf(source) })),
    onSuccess: invalidate,
  });
}

/**
 * Route agent summary (name + linked-source count). Own key: `["ws", id, "agents"]` is shared by
 * other feature groups with a different payload shape.
 */
export function useAgentSummary(agentId: string | null) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: ["ws", wsId, "knowledge", "agent-summaries"],
    queryFn: async () => (await unwrap(api.GET("/v1/agents"))).items,
    select: (items) => items.find((agent) => agent.id === agentId),
    enabled: Boolean(agentId),
    staleTime: 60_000,
  });
}
